import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Button from '@components/ui/Button';
import { RiCheckboxCircleLine, RiAlertLine, RiLoader4Line, RiMailLine } from "@remixicon/react";

export default function EmailConfirmation() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;

    const handleEmailConfirmation = async () => {
      try {
        const hasCodeParam = new URLSearchParams(window.location.search).has('code');

        // detectSessionInUrl (configured in supabase.js) automatically handles
        // the ?code=xxx exchange via PKCE. We just listen for the result.
        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          if (event === 'SIGNED_IN' && session) {
            setStatus('success');

            const isOAuthUser = session?.user?.app_metadata?.provider &&
                               ['google', 'azure'].includes(session.user.app_metadata.provider);

            setTimeout(() => {
              if (mounted) {
                navigate(isOAuthUser ? '/dashboard' : '/mfa-enroll');
              }
            }, 2000);
          }
        });

        authSubscription = data.subscription;

        // Check if session was already established (e.g. detectSessionInUrl processed it)
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData.session) {
          setStatus('success');

          const isOAuthUser = sessionData.session?.user?.app_metadata?.provider &&
                             ['google', 'azure'].includes(sessionData.session.user.app_metadata.provider);

          setTimeout(() => {
            if (mounted) {
              navigate(isOAuthUser ? '/dashboard' : '/mfa-enroll');
            }
          }, 2000);
        } else if (hasCodeParam) {
          // Code was in URL but no session yet — detectSessionInUrl may still be processing.
          // Wait a bit, then check again. If still no session, email was confirmed server-side
          // but the PKCE code_verifier was lost (e.g. different tab/browser).
          setTimeout(async () => {
            if (!mounted) return;
            const { data: retryData } = await supabase.auth.getSession();
            if (retryData.session) {
              setStatus('success');
              const isOAuthUser = retryData.session?.user?.app_metadata?.provider &&
                                 ['google', 'azure'].includes(retryData.session.user.app_metadata.provider);
              setTimeout(() => {
                if (mounted) navigate(isOAuthUser ? '/dashboard' : '/mfa-enroll');
              }, 2000);
            } else {
              // Email is confirmed server-side but session couldn't be established.
              setStatus('confirmed_no_session');
            }
          }, 2000);
        } else {
          setStatus('pending');
        }

      } catch (err) {
        console.error('Email confirmation error:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    handleEmailConfirmation();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate]);

  return (
    <>
      <div className="text-center lg:text-left mb-6">
        <h2 className="text-3xl font-bold text-primary-500">Email Confirmation</h2>
      </div>

      {status === 'verifying' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-8">
            <RiLoader4Line className="h-8 w-8 text-primary-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-600 text-center">Verifying your email...</p>
        </div>
      )}

      {status === 'success' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiCheckboxCircleLine className="h-12 w-12 text-green-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Email confirmed successfully!
          </p>
          <p className="text-sm text-gray-600 text-center">
            Redirecting to MFA enrollment...
          </p>
        </div>
      )}

      {status === 'pending' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiMailLine className="h-12 w-12 text-primary-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Please check your email and click the confirmation link.
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}

      {status === 'confirmed_no_session' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiCheckboxCircleLine className="h-12 w-12 text-green-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Email confirmed successfully!
          </p>
          <p className="text-sm text-gray-600 text-center">
            Please sign in to continue.
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiAlertLine className="h-12 w-12 text-red-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Error confirming email: {error}
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
